<template>
  <div>
    <div class="canvas-wrapper">
      <canvas ref="canvas" id="canvas"></canvas>
    </div>
    <button ref="startButton" @click="start" style="display: none;">Start</button>
  </div>
</template>

<script>
import * as dat from 'dat.gui';

export default {
  name: 'VoiceVisualizer',
  data() {
    return {
      context: null,
      analyser: null,
      freqs: null,
      opts: {
        smoothing: 0.80,
        fft: 8,
        minDecibels: -60,
        scale: 0.25,
        glow: 10,
        color1: [31, 39, 77],
        color2: [41, 200, 192],
        color3: [24, 137, 218],
        fillOpacity: 0.6,
        lineWidth: 1,
        blend: "screen",
        shift: 50,
        width: 60,
        amp: 1
      }
    };
  },
  mounted() {
    // this.initVisualizer();
    setTimeout(this.start, 500);
    setTimeout(() => this.$refs.startButton.style.display = 'block', 500);
  },
  methods: {
    initVisualizer() {
      const gui = new dat.GUI();
      gui.close();
      gui.addColor(this.opts, "color1");
      gui.addColor(this.opts, "color2");
      gui.addColor(this.opts, "color3");
      gui.add(this.opts, "fillOpacity", 0, 1);
      gui.add(this.opts, "lineWidth", 0, 10).step(1);
      gui.add(this.opts, "glow", 0, 130);
      gui.add(this.opts, "blend", ["normal", "multiply", "screen", "overlay", "lighten", "difference"]);
      gui.add(this.opts, "smoothing", 0, 1);
      gui.add(this.opts, "minDecibels", -100, 0);
      gui.add(this.opts, "amp", 0, 6);
      gui.add(this.opts, "width", 0, 60);
      gui.add(this.opts, "shift", 0, 250);
    },
    onStream(stream) {
      const input = this.context.createMediaStreamSource(stream);
      input.connect(this.analyser);
      requestAnimationFrame(this.visualize);
    },
    onStreamError(e) {
      document.body.innerHTML = "<h1>This pen only works with https://</h1>";
      console.error(e);
    },
    range(i) {
      return Array.from(Array(i).keys());
    },
    freq(channel, i) {
      const band = 2 * channel + [1, 3, 0, 4, 2][i] * 6;
      return this.freqs[band];
    },
    scale(i) {
      const x = Math.abs(2 - i);
      const s = 3 - x;
      return s / 3 * this.opts.amp;
    },
    path(channel) {
      const color = this.opts[`color${channel + 1}`].map(Math.floor);
      const ctx = this.$refs.canvas.getContext('2d');
      ctx.fillStyle = `rgba(${color}, ${this.opts.fillOpacity})`;
      ctx.strokeStyle = ctx.shadowColor = `rgb(${color})`;
      ctx.lineWidth = this.opts.lineWidth;
      ctx.shadowBlur = this.opts.glow;
      ctx.globalCompositeOperation = this.opts.blend;
      const m = 400 / 2;
      const offset = (1000 - 15 * this.opts.width) / 2;
      const x = this.range(15).map(i => offset + channel * this.opts.shift + i * this.opts.width);
      const y = this.range(5).map(i => Math.max(0, m - this.scale(i) * this.freq(channel, i)));
      const h = 2 * m;
      ctx.beginPath();
      ctx.moveTo(0, m);
      ctx.lineTo(x[0], m + 1);
      ctx.bezierCurveTo(x[1], m + 1, x[2], y[0], x[3], y[0]);
      ctx.bezierCurveTo(x[4], y[0], x[4], y[1], x[5], y[1]);
      ctx.bezierCurveTo(x[6], y[1], x[6], y[2], x[7], y[2]);
      ctx.bezierCurveTo(x[8], y[2], x[8], y[3], x[9], y[3]);
      ctx.bezierCurveTo(x[10], y[3], x[10], y[4], x[11], y[4]);
      ctx.bezierCurveTo(x[12], y[4], x[12], m, x[13], m);
      ctx.lineTo(1000, m + 1);
      ctx.lineTo(x[13], m - 1);
      ctx.bezierCurveTo(x[12], m, x[12], h - y[4], x[11], h - y[4]);
      ctx.bezierCurveTo(x[10], h - y[4], x[10], h - y[3], x[9], h - y[3]);
      ctx.bezierCurveTo(x[8], h - y[3], x[8], h - y[2], x[7], h - y[2]);
      ctx.bezierCurveTo(x[6], h - y[2], x[6], h - y[1], x[5], h - y[1]);
      ctx.bezierCurveTo(x[4], h - y[1], x[4], h - y[0], x[3], h - y[0]);
      ctx.bezierCurveTo(x[2], h - y[0], x[1], m, x[0], m);
      ctx.lineTo(0, m);
      ctx.fill();
      ctx.stroke();
    },
    visualize() {
      this.analyser.smoothingTimeConstant = this.opts.smoothing;
      this.analyser.fftSize = Math.pow(2, this.opts.fft);
      this.analyser.minDecibels = this.opts.minDecibels;
      this.analyser.maxDecibels = 0;
      this.analyser.getByteFrequencyData(this.freqs);
      const canvas = this.$refs.canvas;
      canvas.width = 1000;
      canvas.height = 400;
      this.path(0);
      this.path(1);
      this.path(2);
      requestAnimationFrame(this.visualize);
    },
    start() {
      this.context = new AudioContext();
      this.analyser = this.context.createAnalyser();
      this.freqs = new Uint8Array(this.analyser.frequencyBinCount);
      this.$refs.startButton.remove();
      navigator.mediaDevices.getUserMedia({ audio: true }).then(this.onStream).catch(this.onStreamError);
    },
    toggleSyncAnimation(isSpeaking) {
      const canvas = this.$refs.canvas;
      if (isSpeaking) {
        canvas.classList.add("speaking");
      } else {
        canvas.classList.remove("speaking");
      }
    }
  }
};
</script>

<style scoped>
@import '../assets/css/style.css';

.canvas-wrapper {
  position: fixed;
  top: 718px;
  left: 0px;
  width: 1920px;
  height: 30%;
  z-index: -1; /* Ensure the canvas is in the background */
  overflow: hidden;
}

#canvas {
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  width: 1920px;
  height: 220px;
  background: radial-gradient(farthest-side, #0c161b00 0%, #13212900 100%) no-repeat fixed 0 0;
  margin: 0;
}

button {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translate3d(-50%,-50%,0);
  font-size: 2vw;
  border-radius: 9em;
  padding: 0.5em 1.5em;
  border: none;
  background: rgba(255,255,255,0.8);
}

.colorful-animation {
  animation: colorChange 2s infinite;
}

@keyframes colorChange {
  0% {
    color: #ff0000;
  }
  50% {
    color: #00ff00;
  }
  100% {
    color: #0000ff;
  }
}

.speaking {
  animation: flicker 0.5s infinite alternate;
}

@keyframes flicker {
  0% {
    background-color: rgba(255, 255, 255, 0.1);
  }
  100% {
    background-color: rgba(255, 255, 255, 0.3);
  }
}
@media (max-width: 768px) {
  #canvas {
    height: 200px;
  }

  button {
    font-size: calc(10px + 2vw);
    padding: 0.4em 1.2em;
  }
}

@media (max-width: 480px) {
  #canvas {
    height: 150px;
  }

  button {
    font-size: calc(8px + 2vw);
    padding: 0.3em 1em;
  }
}
</style>
